import React, { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { checkAddress } from '@r1-frontend/entities/ContactAddress/actions/checkAddress';
import { fillAddressAction } from '@r1-frontend/entities/ContactAddress/actions/fillAddress';
import { setAddressEdit } from '@r1-frontend/entities/ContactAddress/actions/setAddressEdit';
import { EAvailableSpeed } from '@r1-frontend/entities/ContactAddress/initialState';
import { isAddressFilled } from '@r1-frontend/entities/ContactAddress/selectors/isAddressFilled';
import { EVerificationStatus } from '@r1-frontend/entities/ContactDetail/initialState';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import { RoundedBlock } from '@r1-frontend/ui-react/experimental/blocks';
import { FullWidthListContainer, ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { IAddress } from '@r1-frontend/shared/types/IAddress';

import AddressInputFields from '~/src/components/AddressInputFields';
import { POLICY_PAGE_LINK } from '~/src/constants';
import { GIGABIT_SPEED, MEGABIT_SPEED } from '~/src/features/full-buy/consts';
import { ConfirmedForm } from '~/src/features/full-buy/ContactAddress/ConfirmedForm';
import { setSpeedRestrictions, setStep } from '~/src/features/tariffBuilder/actions';
import { constructorAnalytics } from '~/src/features/tariffBuilder/analytics';
import { AddressCheckBlock } from '~/src/features/tariffBuilder/components/AddressCheckBlock';
import { GA_CATEGORY } from '~/src/features/tariffBuilder/constants/analytics';
import { useCtxDispatch, useCtxSelector } from '~/src/features/tariffBuilder/hooks';
import getSelectedEntity from '~/src/features/tariffBuilder/selectors/getSelectedEntity';
import { ESteps } from '~/src/features/tariffBuilder/types/ESteps';
import { TState } from '~/src/store';

const AddressContainer = ({ ...props }): JSX.Element => {
    const localDispatch = useCtxDispatch();
    const globalDispatch = useDispatch();
    const { speed } = useCtxSelector(getSelectedEntity);

    const {
        contactAddress: {
            isEdit,
            verifiedAddressData: {
                isHouseConnected,
                isPrivate,
                status: addressStatus,
                address: verifiedAddress,
                availableSpeed,
            },
        },
        isConfirmButtonActive,
    } = useSelector((state: TState) => ({
        contactAddress: state.contactAddress,
        isConfirmButtonActive: isAddressFilled(state),
    }), shallowEqual);

    const currentSpeedAcrossEnum = useMemo(() => {
        if (speed === null) {
            return null;
        }

        if (Number(speed) >= GIGABIT_SPEED) {
            return EAvailableSpeed.more1000;
        }

        if (Number(speed) > MEGABIT_SPEED) {
            return EAvailableSpeed.between100and1000;
        }

        return EAvailableSpeed.lessOrEqual100;
    }, [speed]);

    const shouldConnect = isHouseConnected && addressStatus === EVerificationStatus.verified && (
        (Number(currentSpeedAcrossEnum) > EAvailableSpeed.lessOrEqual100 && Number(currentSpeedAcrossEnum) <= availableSpeed) ||
        (Number(currentSpeedAcrossEnum) === EAvailableSpeed.lessOrEqual100)
    );

    const checkAddressData = () => {
        constructorAnalytics.addressVerification.clickedAddressVerification();

        globalDispatch(checkAddress(false, false, (result) => constructorAnalytics.addressVerification.addressVerification(result)));
        globalDispatch(setAddressEdit(false));
    };

    const onEdit = useCallback(() => {
        globalDispatch(setAddressEdit(true));
    }, [globalDispatch]);

    const onFillAddress = useCallback((address: IAddress) => {
        globalDispatch(fillAddressAction(address));
    }, [globalDispatch]);

    const handleGigabitBtnClick = () => {
        localDispatch(setSpeedRestrictions({
            min: availableSpeed === EAvailableSpeed.lessOrEqual100 ? 100 : undefined,
            max: availableSpeed === EAvailableSpeed.lessOrEqual100 ? 100 : availableSpeed === EAvailableSpeed.between100and1000 ? 900 : undefined,
        }));
        localDispatch(setStep(ESteps.SpeedAndTv));
    };

    const nextStepHandler = () => {
        localDispatch(setStep(ESteps.FillContacts));
        constructorAnalytics.addressVerification.confirmCheckAddress();
    };

    return (
        <div {...props}>
            {isEdit && <>
                <TextWrapper>
                    <H3>Проверьте, доступен ли тариф «Конструктор {speed} Мбит/с» по вашему адресу</H3>
                </TextWrapper>
                <FullWidthListContainer $size={16}>
                    <RoundedBlock $bgColor={COLORS.BgSurface} $indent={'24px'}>
                        <ListContainer $size={16}>
                            <AddressInputFields
                                catchAddressData={onFillAddress}
                                disableCityChange={true}
                                gtmCategory={GA_CATEGORY}
                                gtmSenderCallback={(action) => constructorAnalytics.addressVerification.addressInputFieldsCallback(action)}
                                defaultAddress={verifiedAddress}
                                withSatelliteFilter={false}
                            />
                            <PolicyNote
                                buttonText='Подтвердить'
                                agreeUrl={POLICY_PAGE_LINK}
                            />
                        </ListContainer>
                    </RoundedBlock>
                    <ButtonGroup>
                        <Button bType={'action'} onClick={checkAddressData} disabled={!isConfirmButtonActive}>Подтвердить</Button>
                    </ButtonGroup>
                </FullWidthListContainer>
                </>
            }
            {!isEdit && <FullWidthListContainer $size={16}>
                <AddressCheckBlock
                    gigabitMayConnect={Number(currentSpeedAcrossEnum) <= availableSpeed && availableSpeed > EAvailableSpeed.lessOrEqual100}
                    requiredGigaSpeed={Number(currentSpeedAcrossEnum) > EAvailableSpeed.lessOrEqual100}
                    isHouseConnected={isHouseConnected}
                    speed={speed}
                    isNotFlatAddress={Boolean(isPrivate)}
                    addressStatus={addressStatus}
                    handleGigabitBtnClick={handleGigabitBtnClick}
                    availableSpeed={availableSpeed}
                />
                <RoundedBlock $bgColor={COLORS.BgSurface} $indent={'24px'}>
                    <ConfirmedForm
                        isDisableBtn={addressStatus === EVerificationStatus.verifying}
                        onEdit={onEdit}
                    />
                </RoundedBlock>
                {shouldConnect && <ButtonGroup>
                    <Button bType={'action'} onClick={nextStepHandler}>Продолжить</Button>
                </ButtonGroup>}
            </FullWidthListContainer>
            }
        </div>
    );
};

export default styled(AddressContainer)`
  ${RoundedBlock} {
    width: 100%;
  }
  
  width: 100%;
`;
